import { FC, useEffect, useState } from "react"
import dayjs from "dayjs"

import { useTranslation } from "react-i18next"

import { settingsService } from "services/settings/settings.service"
import { SettingsHistoryEntry } from "services/settings/type"

interface SettingsHistoryNoteProps {
  /** Registry id of the settings group, e.g. `customerio`. */
  target: string
  /** Bump to refetch after a save or a reset. */
  refreshKey?: number
}

/**
 * "Last changed by X" for one settings group.
 *
 * Answers the question behind a "my settings reverted" report without anyone having to
 * open the activity page and filter it by hand.
 */
export const SettingsHistoryNote: FC<SettingsHistoryNoteProps> = ({ target, refreshKey = 0 }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [entry, setEntry] = useState<SettingsHistoryEntry | null>(null)

  useEffect(() => {
    let active = true

    settingsService.getSettingHistory(target, 1).then((entries) => {
      if (active) setEntry(entries[0] ?? null)
    })

    return () => {
      active = false
    }
  }, [target, refreshKey])

  if (!entry) return null

  const actionKey = `settings.history.actions.${entry.action.toLowerCase()}`
  const action = t(actionKey, { defaultValue: entry.action })

  return (
    <p className="text-xs text-gray-500 dark:text-gray-400">
      {t("settings.history.lastChanged", {
        action,
        admin: entry.admin.username ?? t("settings.history.unknownAdmin"),
        when: dayjs(entry.created_at).format("YYYY-MM-DD HH:mm")
      })}
    </p>
  )
}
